import { useState, type FormEvent } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Loader2, Globe, Camera, Terminal, AlertCircle, CheckCircle2,
  FileText, Network, Plug, KeyRound, Radio, Check, Copy, Download,
  Eye, EyeOff, type LucideIcon,
} from "lucide-react";
import {
  CONNECTOR_KINDS_BY_DEVICE_KIND, useCreateConnector, useTenantSshKey,
  type Device, type ConnectorKind, type VerifyResult,
} from "@/lib/queries";
import { cn } from "@/lib/utils";

// Display metadata per connector kind. Description copy keeps the form
// minimal — the operator picks by reading what each kind does.
const KIND_META: Record<ConnectorKind, {
  label: string;
  blurb: string;
  icon: LucideIcon;
}> = {
  ssh: {
    label: "SSH",
    blurb: "Tyrael runs read-only commands via the tenant SSH key. Adopt this once the public key is in authorized_keys on the host.",
    icon: KeyRound,
  },
  filebeat: {
    label: "Filebeat",
    blurb: "Log shipper to Graylog (legacy provisioning predates Tyrael). Adopt this if filebeat is already configured externally.",
    icon: FileText,
  },
  netflow: {
    label: "NetFlow",
    blurb: "Flow records exported by the device, ingested into the flows table. Adopt this once /ip traffic-flow points at the collector.",
    icon: Network,
  },
  snmp: {
    label: "SNMP",
    blurb: "Periodic SNMP polls. Provide community + version. Adopt-only for now — runtime polling is not yet wired.",
    icon: Plug,
  },
  api: {
    label: "API",
    blurb: "Generic REST/HTTP integration. Free-form endpoint. Adopt-only metadata; runtime calls are device-kind specific.",
    icon: Globe,
  },
  http_digest: {
    label: "HTTP Digest (admin API)",
    blurb: "How Tyrael talks to the device's admin API. Required for Hikvision NVR/DVR/MinMoe — also unlocks the syslog auto-provisioner.",
    icon: Camera,
  },
  shell_command: {
    label: "Shell command",
    blurb: "Periodic SSH command, output captured into device_command_outputs.",
    icon: Terminal,
  },
  http_poll: {
    label: "HTTP poll",
    blurb: "Periodic HTTP GET, response captured into device_command_outputs.",
    icon: Globe,
  },
  log_tail: {
    label: "Log tail",
    blurb: "Add log paths to the device's filebeat shipper. Requires filebeat to already be active.",
    icon: FileText,
  },
  syslog: {
    label: "Syslog",
    blurb: "BSD syslog to siem.nws.ro:514. Auto-provisioned via ISAPI on Hikvision; declare-only on other kinds.",
    icon: Radio,
  },
};

// Per-kind config form. Each returns the config dict to send to the API.
// For kinds not yet provisioned (snmp, api), config is purely
// declarative — operator-supplied metadata.
// Shared rendering of a VerifyResult — also reused by DeviceDetail's
// per-card Verify button.
export function VerifyResultPanel({
  result, onDone, onRetry,
}: {
  result: VerifyResult;
  onDone: () => void;
  onRetry?: () => void;
}) {
  const Icon = result.ok ? CheckCircle2 : AlertCircle;
  const tone = result.ok
    ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-900 dark:text-emerald-200"
    : "border-red-500/40 bg-red-500/5 text-red-900 dark:text-red-200";

  return (
    <div className="space-y-4">
      <div className={cn("rounded-md border p-3", tone)}>
        <div className="flex items-start gap-2">
          <Icon className={cn(
            "mt-0.5 h-4 w-4 shrink-0",
            result.ok ? "text-emerald-600 dark:text-emerald-400"
                      : "text-red-600 dark:text-red-400",
          )} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">
              {result.ok ? "Verified" : "Verification failed"}
            </div>
            <div className="mt-0.5 text-sm">{result.message}</div>
          </div>
        </div>
      </div>

      {result.remediation && !result.ok && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
          <div className="text-xs font-medium uppercase tracking-wide text-amber-700 dark:text-amber-400">
            How to fix
          </div>
          <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-[11px] text-amber-900 dark:text-amber-200">
            {result.remediation}
          </pre>
        </div>
      )}

      {result.evidence && Object.keys(result.evidence).length > 0 && (
        <div>
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Evidence
          </div>
          <pre className="overflow-x-auto rounded-md border border-border bg-muted/40 p-2 font-mono text-[11px]">
            {JSON.stringify(result.evidence, null, 2)}
          </pre>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-2">
        {onRetry && !result.ok && (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent"
          >
            Edit config
          </button>
        )}
        <button
          type="button"
          onClick={onDone}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Done
        </button>
      </div>
    </div>
  );
}

// Small reusable copy-to-clipboard one-liner block. Used by both the
// Linux and MikroTik install flows.
function CopyableCommand({ cmd }: { cmd: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(cmd);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };
  return (
    <div className="flex items-start gap-2">
      <pre className="flex-1 overflow-x-auto rounded-md border border-border bg-muted/40 p-2 font-mono text-[11px]">
        {cmd}
      </pre>
      <button
        type="button"
        onClick={onCopy}
        title="Copy"
        className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md border border-border bg-background px-2 text-[11px] hover:bg-accent"
      >
        {copied
          ? <><Check className="h-3 w-3" /> Copied</>
          : <><Copy className="h-3 w-3" /> Copy</>}
      </button>
    </div>
  );
}

// Trigger a browser download of a string as a file. Used to save the
// tenant pubkey as `tyrael_tenant.pub` so MikroTik admins can drop it
// onto the router via WinBox or scp it from their workstation.
function downloadAsFile(filename: string, contents: string) {
  const blob = new Blob([contents.endsWith("\n") ? contents : contents + "\n"], {
    type: "text/plain",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function LinuxSshInstall({
  device, pubKey,
}: {
  device: Device;
  pubKey: string;
}) {
  const cmd = (
    `mkdir -p ~/.ssh && echo '${pubKey}' >> ~/.ssh/authorized_keys ` +
    `&& chmod 700 ~/.ssh && chmod 600 ~/.ssh/authorized_keys`
  );
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-muted-foreground">
        Install command (run as <code className="font-mono">{device.ssh_user}</code> on the host)
      </label>
      <CopyableCommand cmd={cmd} />
    </div>
  );
}

// RouterOS doesn't have ~/.ssh/authorized_keys. Keys live in
// `/user ssh-keys` and only land via `/user ssh-keys import`, which
// requires the public key already be present as a file on the router.
// So the install is a two-step: upload the .pub file, then import it.
// We give two paths because MikroTik admins live in two different
// worlds — CLI (scp + ssh) or WinBox (drag-and-drop + terminal).
function MikrotikSshInstall({
  device, pubKey,
}: {
  device: Device;
  pubKey: string;
}) {
  const host = device.hostname ?? device.name;
  const user = device.ssh_user;
  const port = device.ssh_port;

  const scpCmd     = `scp tyrael_tenant.pub ${user}@${host}:tyrael_tenant.pub`;
  const sshImport  = `ssh -p ${port} ${user}@${host} '/user ssh-keys import public-key-file=tyrael_tenant.pub user=${user}'`;
  const localImport = `/user ssh-keys import public-key-file=tyrael_tenant.pub user=${user}`;

  return (
    <div className="space-y-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
      <div className="text-xs text-amber-900 dark:text-amber-200">
        <strong>RouterOS quirk.</strong> MikroTik doesn't have <code className="font-mono">~/.ssh/authorized_keys</code>.
        Keys live in <code className="font-mono">/user ssh-keys</code> and only land via{" "}
        <code className="font-mono">/user ssh-keys import</code>, which needs the public key
        as a file on the router first. Step 1: get the file onto the router. Step 2: import.
      </div>

      <button
        type="button"
        onClick={() => downloadAsFile("tyrael_tenant.pub", pubKey)}
        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
      >
        <Download className="h-3.5 w-3.5" />
        Download <code className="font-mono">tyrael_tenant.pub</code>
      </button>

      <div className="space-y-1.5">
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Path A — from your terminal
        </div>
        <CopyableCommand cmd={scpCmd} />
        <CopyableCommand cmd={sshImport} />
      </div>

      <div className="space-y-1.5">
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Path B — from WinBox
        </div>
        <ol className="ml-4 list-decimal space-y-1 text-xs text-foreground">
          <li>Click <strong>Download</strong> above to save <code className="font-mono">tyrael_tenant.pub</code>.</li>
          <li>In WinBox, open the <strong>Files</strong> panel (left sidebar) and drag the file onto the router.</li>
          <li>Open <strong>New Terminal</strong> (top menu) and paste this — it tells RouterOS to authorize the public key for user <code className="font-mono">{user}</code>:</li>
        </ol>
        <CopyableCommand cmd={localImport} />
        <p className="text-[11px] leading-snug text-muted-foreground">
          <strong>Don't</strong> use the WinBox <em>SSH Settings → Import Host Key</em> dialog — that's
          for the router's <em>own</em> private key (its server identity), and it'll reject this
          public key with <em>"unable to load key file"</em>. The right place is{" "}
          <strong>System → Users → SSH Keys → Import</strong>, or just the terminal command above.
        </p>
      </div>
    </div>
  );
}

function ConfigFormSsh({ device }: { device: Device }) {
  // We only need the key while the SSH form is mounted, so let the
  // hook fetch lazily with the standard 5-min staleTime — opening the
  // sheet doesn't poke /tenant/ssh-key on every render.
  const keyQ = useTenantSshKey(true);

  const target = `${device.ssh_user}@${device.hostname ?? device.name}:${device.ssh_port}`;

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
        <p className="text-muted-foreground">
          Tyrael will SSH as <code className="font-mono">{target}</code> using the tenant key.
          When you click <strong>Add connector</strong>, an automatic probe runs:
          green if the public key is authorized on the device, red with the install
          instructions surfaced inline if it isn't.
        </p>
      </div>

      {keyQ.isLoading && <div className="text-xs text-muted-foreground">Loading tenant key…</div>}

      {keyQ.data && (
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Tenant public key (fingerprint <code className="font-mono">{keyQ.data.fingerprint}</code>)
            </label>
            <pre className="overflow-x-auto rounded-md border border-border bg-muted/40 p-2 font-mono text-[11px]">
              {keyQ.data.public_key}
            </pre>
          </div>

          {device.kind === "mikrotik"
            ? <MikrotikSshInstall device={device} pubKey={keyQ.data.public_key} />
            : <LinuxSshInstall    device={device} pubKey={keyQ.data.public_key} />}
        </div>
      )}
    </div>
  );
}

function ConfigFormNetflow({ value, onChange }: ConfigFormProps) {
  return (
    <div className="space-y-3">
      <Field
        label="Collector"
        hint="Where the device sends NetFlow records. Defaults to the Tyrael VM's goflow2 listener."
      >
        <input
          type="text"
          value={(value.collector as string) ?? "tyrael.nws.ro:2055/udp"}
          onChange={(e) => onChange({ ...value, collector: e.target.value })}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm"
        />
      </Field>
      <Field label="Format">
        <select
          value={(value.format as string) ?? "NetFlow v9 / IPFIX"}
          onChange={(e) => onChange({ ...value, format: e.target.value })}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
        >
          <option>NetFlow v9 / IPFIX</option>
          <option>NetFlow v5</option>
          <option>sFlow v5</option>
        </select>
      </Field>
    </div>
  );
}

function ConfigFormSnmp({ value, onChange }: ConfigFormProps) {
  return (
    <div className="space-y-3">
      <Field label="Community" hint="SNMP community string (read-only).">
        <input
          type="text"
          value={(value.community as string) ?? "public"}
          onChange={(e) => onChange({ ...value, community: e.target.value })}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm"
        />
      </Field>
      <Field label="Version">
        <select
          value={(value.version as string) ?? "v2c"}
          onChange={(e) => onChange({ ...value, version: e.target.value })}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
        >
          <option value="v1">v1</option>
          <option value="v2c">v2c</option>
          <option value="v3">v3</option>
        </select>
      </Field>
    </div>
  );
}

// Reusable Username + Password input pair. Shows the eye toggle on the
// password so operators can sanity-check what they typed before hitting
// Add. Backend auto-promotes these into tenant_secrets on save — the
// plaintext values never persist on device_connectors.config.
function CredentialFields({
  username, password, onUsername, onPassword,
}: {
  username: string;
  password: string;
  onUsername: (next: string) => void;
  onPassword: (next: string) => void;
}) {
  const [reveal, setReveal] = useState(false);
  return (
    <>
      <Field label="Username">
        <input
          type="text" autoComplete="off"
          placeholder="admin"
          value={username}
          onChange={(e) => onUsername(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm"
        />
      </Field>
      <Field label="Password" hint="Stored encrypted on the server. Plaintext is never persisted on the connector row.">
        <div className="relative">
          <input
            type={reveal ? "text" : "password"} autoComplete="new-password"
            value={password}
            onChange={(e) => onPassword(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-1.5 pr-9 font-mono text-sm"
          />
          <button
            type="button" tabIndex={-1}
            onClick={() => setReveal((r) => !r)}
            title={reveal ? "Hide" : "Show"}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            {reveal ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        </div>
      </Field>
    </>
  );
}

function ConfigFormApi({ value, onChange }: ConfigFormProps) {
  const auth = (value.auth as string) ?? "none";
  return (
    <div className="space-y-3">
      <Field label="Endpoint" hint="Base URL of the device's API.">
        <input
          type="text"
          placeholder="https://device.local/api"
          value={(value.endpoint as string) ?? ""}
          onChange={(e) => onChange({ ...value, endpoint: e.target.value })}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm"
        />
      </Field>
      <Field label="Auth" hint="None = unauthenticated. Most device admin APIs need basic or digest.">
        <select
          value={auth}
          onChange={(e) => onChange({ ...value, auth: e.target.value })}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
        >
          <option value="none">none</option>
          <option value="basic">basic</option>
          <option value="digest">digest</option>
        </select>
      </Field>
      {(auth === "basic" || auth === "digest") && (
        <CredentialFields
          username={(value.username as string) ?? ""}
          password={(value.password as string) ?? ""}
          onUsername={(u) => onChange({ ...value, username: u })}
          onPassword={(p) => onChange({ ...value, password: p })}
        />
      )}
      <Field label="Notes (optional)">
        <input
          type="text"
          value={(value.note as string) ?? ""}
          onChange={(e) => onChange({ ...value, note: e.target.value })}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
        />
      </Field>
    </div>
  );
}

function ConfigFormHttpDigest({ value, onChange }: ConfigFormProps) {
  return (
    <div className="space-y-3">
      <Field
        label="URL"
        hint="Where Tyrael will send ISAPI / digest-auth requests. For Hikvision NVR/DVR this is the device's web admin URL — e.g. http://5.2.225.55:8000."
      >
        <input
          type="text"
          placeholder="http://5.2.225.55:8000"
          value={(value.url as string) ?? ""}
          onChange={(e) => onChange({ ...value, url: e.target.value })}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm"
        />
      </Field>
      <CredentialFields
        username={(value.username as string) ?? ""}
        password={(value.password as string) ?? ""}
        onUsername={(u) => onChange({ ...value, username: u })}
        onPassword={(p) => onChange({ ...value, password: p })}
      />
    </div>
  );
}

function ConfigFormShellCommand({ value, onChange }: ConfigFormProps) {
  return (
    <div className="space-y-3">
      <Field label="Command" hint="Shell command to run via SSH on each tick.">
        <input
          type="text"
          placeholder="systemctl status filebeat"
          value={(value.command as string) ?? ""}
          onChange={(e) => onChange({ ...value, command: e.target.value })}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm"
        />
      </Field>
      <Field label="Interval (seconds)" hint="Min 60, max 86400.">
        <input
          type="number"
          min={60} max={86400}
          value={(value.interval_s as number) ?? 300}
          onChange={(e) => onChange({ ...value, interval_s: Number(e.target.value) })}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm"
        />
      </Field>
    </div>
  );
}

function ConfigFormHttpPoll({ value, onChange }: ConfigFormProps) {
  return (
    <div className="space-y-3">
      <Field label="URL">
        <input
          type="text"
          value={(value.url as string) ?? ""}
          onChange={(e) => onChange({ ...value, url: e.target.value })}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm"
        />
      </Field>
      <Field label="Interval (seconds)" hint="Min 30, max 86400.">
        <input
          type="number"
          min={30} max={86400}
          value={(value.interval_s as number) ?? 60}
          onChange={(e) => onChange({ ...value, interval_s: Number(e.target.value) })}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm"
        />
      </Field>
      <Field label="Auth">
        <select
          value={(value.auth as string) ?? "none"}
          onChange={(e) => onChange({ ...value, auth: e.target.value })}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
        >
          <option value="none">none</option>
          <option value="basic">basic</option>
          <option value="digest">digest</option>
        </select>
      </Field>
      {((value.auth as string) === "basic" || (value.auth as string) === "digest") && (
        <CredentialFields
          username={(value.username as string) ?? ""}
          password={(value.password as string) ?? ""}
          onUsername={(u) => onChange({ ...value, username: u })}
          onPassword={(p) => onChange({ ...value, password: p })}
        />
      )}
    </div>
  );
}

function ConfigFormLogTail({ value, onChange }: ConfigFormProps) {
  const pathsArr = (value.paths as string[]) ?? [];
  return (
    <div className="space-y-3">
      <Field label="Paths" hint="One path per line. Filebeat will be reconfigured to tail these.">
        <textarea
          rows={5}
          placeholder="/var/log/exim_mainlog\n/var/log/exim_rejectlog"
          value={pathsArr.join("\n")}
          onChange={(e) => onChange({
            ...value,
            paths: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean),
          })}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm"
        />
      </Field>
    </div>
  );
}

// BSD syslog config — same shape on every kind. The HIKVISION case
// shows an extra panel announcing the auto-provision flow + a
// dependency check that the http_digest connector is active (the
// auto-provisioner reuses its credentials). All other kinds are
// declare-only: operator points the device at siem.nws.ro:514 in the
// device's own config (rsyslog, /system logging action, etc).
function ConfigFormSyslog({
  device, value, onChange,
}: {
  device: Device;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const isHik = device.kind === "hikvision";
  const httpDigest = (device.connectors ?? []).find((c) => c.kind === "http_digest");
  const httpDigestActive = httpDigest?.status === "active";

  const server   = (value.server   as string) ?? "siem.nws.ro";
  // 5140 not 514: NRG Graylog binds the Syslog UDP input on 5140 (the
  // backend default mirrors this — keep them in sync).
  const port     = (value.port     as number) ?? 5140;
  const protocol = (value.protocol as string) ?? "UDP";

  return (
    <div className="space-y-3">
      {isHik ? (
        <div className="rounded-md border border-blue-500/40 bg-blue-500/5 p-3 text-xs text-blue-900 dark:text-blue-200">
          <strong>Auto-provisioned via ISAPI.</strong> When you click <em>Add connector</em>,
          Tyrael writes the syslog target to the device over ISAPI (HTTP Digest). The device
          starts shipping immediately — first message in Graylog usually within 1-5 min.
          Status stays <code className="font-mono">configuring</code> until the verifier sees
          a real message; click <em>Verify</em> on the connector card after a few minutes.
          {!httpDigestActive && (
            <div className="mt-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-2 text-amber-900 dark:text-amber-200">
              <strong>Missing dependency:</strong> this device has no active <code className="font-mono">http_digest</code> connector.
              The auto-provisioner reuses its credentials. Add an <code className="font-mono">http_digest</code> connector first
              (with <code className="font-mono">username_ref</code> / <code className="font-mono">password_ref</code> pointing at <code className="font-mono">tenant_secrets</code>),
              verify it goes green, then come back here.
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-900 dark:text-amber-200">
          <strong>Declare-only.</strong> Tyrael won't reach into a {device.kind} device to set the
          syslog target — point the device at <code className="font-mono">{server}:{port}/{protocol}</code> yourself
          (rsyslog config, MikroTik <code className="font-mono">/system logging action</code>, Windows event forwarding,
          etc.), then click <em>Verify</em> to confirm Graylog is receiving messages from
          <code className="font-mono"> source:{device.name}</code>.
        </div>
      )}

      <Field label="Syslog server" hint="Where the device ships syslog. Defaults to NRG Graylog.">
        <input
          type="text"
          value={server}
          onChange={(e) => onChange({ ...value, server: e.target.value })}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm"
        />
      </Field>
      <Field label="Port">
        <input
          type="number"
          min={1} max={65535}
          value={port}
          onChange={(e) => onChange({ ...value, port: Number(e.target.value) })}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm"
        />
      </Field>
      <Field label="Protocol">
        <select
          value={protocol}
          onChange={(e) => onChange({ ...value, protocol: e.target.value })}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
        >
          <option value="UDP">UDP</option>
          <option value="TCP">TCP</option>
        </select>
      </Field>
    </div>
  );
}

function ConfigFormFilebeat({ value, onChange }: ConfigFormProps) {
  return (
    <div className="space-y-3">
      <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-900 dark:text-amber-200">
        <strong>Adopt-only.</strong> Tyrael will not install or configure filebeat from this dialog —
        use the Add Device wizard for that. This entry just records that filebeat is already running
        on the host (legacy provisioning, externally managed).
      </div>
      <Field label="Note">
        <input
          type="text"
          value={(value.note as string) ?? "legacy provisioning, managed externally"}
          onChange={(e) => onChange({ ...value, note: e.target.value })}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
        />
      </Field>
    </div>
  );
}

interface ConfigFormProps {
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}

function Field({ label, hint, children }: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
      {children}
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function ConfigForm({
  kind, device, value, onChange,
}: {
  kind: ConnectorKind;
  device: Device;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  switch (kind) {
    case "ssh":           return <ConfigFormSsh device={device} />;
    case "filebeat":      return <ConfigFormFilebeat value={value} onChange={onChange} />;
    case "netflow":       return <ConfigFormNetflow value={value} onChange={onChange} />;
    case "snmp":          return <ConfigFormSnmp value={value} onChange={onChange} />;
    case "api":           return <ConfigFormApi value={value} onChange={onChange} />;
    case "http_digest":   return <ConfigFormHttpDigest value={value} onChange={onChange} />;
    case "shell_command": return <ConfigFormShellCommand value={value} onChange={onChange} />;
    case "http_poll":     return <ConfigFormHttpPoll value={value} onChange={onChange} />;
    case "log_tail":      return <ConfigFormLogTail value={value} onChange={onChange} />;
    case "syslog":        return <ConfigFormSyslog device={device} value={value} onChange={onChange} />;
  }
}

export function AddConnectorSheet({
  device, open, onOpenChange,
}: {
  device: Device;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const create = useCreateConnector();
  const [kind, setKind] = useState<ConnectorKind | null>(null);
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);
  // Post-submit verifier result. While this is set the form switches
  // into a "result" view — operator chooses Done (close sheet) or
  // Retry (re-run verify on the connector).
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null);

  const applicable = CONNECTOR_KINDS_BY_DEVICE_KIND[device.kind];
  // Filter out kinds the device already has — UNIQUE(device_id, kind).
  const existing = new Set((device.connectors ?? []).map((c) => c.kind));
  const choices = applicable.filter((k) => !existing.has(k));

  const reset = () => {
    setKind(null);
    setConfig({});
    setError(null);
    setVerifyResult(null);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!kind) return;
    setError(null);
    setVerifyResult(null);
    try {
      const result = await create.mutateAsync({
        deviceId: device.id,
        body: { kind, config },
      });
      // Don't auto-close — show the verify result so the operator
      // sees real feedback. They click Done when satisfied.
      setVerifyResult(result.verify);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't create connector");
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <SheetContent className="sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>Add connector to {device.name}</SheetTitle>
          <SheetDescription>
            Adopt-mode: this just records that the connector exists.
            Tyrael will not contact the host — you provision out-of-band.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {verifyResult !== null ? (
            <VerifyResultPanel
              result={verifyResult}
              onDone={() => { reset(); onOpenChange(false); }}
              onRetry={() => setVerifyResult(null)}
            />
          ) : kind === null ? (
            <div className="space-y-2">
              {choices.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Every applicable connector kind is already attached to this device.
                </p>
              ) : (
                choices.map((k) => {
                  const meta = KIND_META[k];
                  const Icon = meta.icon;
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => { setKind(k); setConfig({}); }}
                      className="flex w-full items-start gap-3 rounded-md border border-border bg-card px-3 py-2.5 text-left transition-colors hover:border-primary/40 hover:bg-primary/5"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium">{meta.label}</div>
                        <div className="text-[11px] leading-snug text-muted-foreground">{meta.blurb}</div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="rounded-md border border-border bg-muted/40 p-3">
                <button
                  type="button"
                  onClick={() => { setKind(null); setConfig({}); }}
                  className="text-[11px] font-medium text-primary hover:underline"
                >
                  ← Choose a different kind
                </button>
                <h3 className="mt-1 text-sm font-semibold">{KIND_META[kind].label}</h3>
                <p className="mt-1 text-xs text-muted-foreground">{KIND_META[kind].blurb}</p>
              </div>

              <ConfigForm kind={kind} device={device} value={config} onChange={setConfig} />

              {error && (
                <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-400">
                  {error}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={create.isPending}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50",
                  )}
                >
                  {create.isPending
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <Check className="h-4 w-4" />}
                  Add connector
                </button>
              </div>
            </form>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

